import React, { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { canDeleteField, useDeleteMultipleFields, useGetAllFieldPracticesByFarm, useGetFarms, useTranslation } from "utils";

import { GET_FIELDS } from "consts";

import { CarbonProgram, FieldListField } from "models";

import { Button, Drawer, Modal, Typography, Message as message } from "components";

import { FieldSelectionList } from "pages/app/fields/components/FieldSelectionList";

type FieldDeletionDrawerProps = {
  fields: FieldListField[];
  visible: boolean;
  onClose: () => void;
  onDelete: () => void;
  carbonProgram?: CarbonProgram | null;
};

export const FieldDeletionDrawer = ({ fields, visible, onClose, onDelete, carbonProgram }: FieldDeletionDrawerProps) => {
  const { t } = useTranslation("farm");
  const queryClient = useQueryClient();
  const { currentFarm } = useGetFarms();
  const { data: allFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const { mutateAsync: deleteMultipleFieldsAsync } = useDeleteMultipleFields();

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);

  const [showDeletionConfirmationPopover, setShowDeletionConfirmationPopover] = useState<boolean>(false);

  const handleOnClose = () => {
    setSelectedFieldIds([]);
    setLoading(false);
    setShowDeletionConfirmationPopover(false);
    onClose();
  };

  const handleFieldDeletion = async () => {
    setLoading(true);

    await deleteMultipleFieldsAsync({
      fieldIds: selectedFieldIds,
    })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: [GET_FIELDS] });
        message({ content: t("fieldDeletionDrawer.fieldDeleted"), icon: "tick" });
        onDelete();
      })
      .catch(() => {
        message({ content: t("fieldDeletionDrawer.fieldDeletionError"), icon: "error" });
      })
      .finally(() => {
        handleOnClose();
      });
  };

  return (
    <>
      <Drawer
        className="field-deletion-drawer"
        footer={
          <Button
            block
            disabled={loading || !selectedFieldIds.length}
            loading={loading}
            onClick={() => setShowDeletionConfirmationPopover(true)}
            type="primary"
          >
            {t("fieldDeletionDrawer.deleteFields")}
          </Button>
        }
        title={t("fieldDeletionDrawer.title")}
        visible={visible}
        onClose={handleOnClose}
      >
        <Typography variant="h2" className="margin-bottom-200">
          {t("fieldDeletionDrawer.explainer")}
        </Typography>

        <FieldSelectionList
          fields={fields}
          isFieldSelectable={(field) => canDeleteField({ availablePracticeBundles: allFieldsPractices?.[field.id]?.availablePracticeBundles, carbonProgram })}
          selectedFields={selectedFieldIds}
          setSelectedFields={setSelectedFieldIds}
        />

        <div className="margin-bottom-250" />
        <Modal
          cancelText={t("farm:fieldDeletionDrawer.cancel")}
          description={t("farm:fieldDeletionDrawer.deleteExplainer")}
          okText={t("farm:fieldDeletionDrawer.deleteFields")}
          onCancel={() => setShowDeletionConfirmationPopover(false)}
          onOk={handleFieldDeletion}
          title={t("farm:fieldDeletionDrawer.title")}
          showModal={showDeletionConfirmationPopover}
        />
      </Drawer>
    </>
  );
};
